import React, { useRef, useEffect, useState, useCallback } from "react";
import ForceGraph2D from "react-force-graph-2d";

const TYPE_COLOR = {
  origin: "#0D5C58", center: "#8BA4B5", person: "#8BA4B5", business: "#64748B", organization: "#127a74",
  place: "#4b6a82", group: "#6d7f91", event: "#5a738a",
};

export default function NetworkGraph({ data, selectedId, highlightPath = [], onSelect }) {
  const wrap = useRef(null);
  const fgRef = useRef(null);
  const [dims, setDims] = useState({ w: 600, h: 500 });

  useEffect(() => {
    if (!wrap.current) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setDims({ w: r.width, h: r.height });
    });
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);

  const graphData = {
    nodes: data.nodes.map((n) => ({ ...n })),
    links: data.links.map((l) => ({ ...l })),
  };

  const pathKey = highlightPath.join(",");

  const paint = useCallback((node, ctx, scale) => {
    const pathSet = new Set(pathKey ? pathKey.split(",") : []);
    const isHub = node.kind === "origin" || node.kind === "center";
    const r = isHub ? 9 : 5;
    const sel = node.id === selectedId;
    const inPath = pathSet.has(node.id);
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = TYPE_COLOR[node.kind === "origin" ? "origin" : node.kind === "center" ? "center" : node.type] || "#64748B";
    ctx.fill();
    if (node.kind === "center") {
      ctx.lineWidth = 2.5 / scale; ctx.strokeStyle = "#F8FAFC"; ctx.stroke();
    }
    if (sel || inPath) {
      ctx.lineWidth = 2 / scale;
      ctx.strokeStyle = sel ? "#F8FAFC" : "#8BA4B5";
      ctx.stroke();
    }
    if (node.verification_status === "disputed") {
      ctx.lineWidth = 1.5 / scale; ctx.strokeStyle = "#EF4444"; ctx.stroke();
    }
    if (scale > 1.2 || isHub) {
      const label = node.label?.length > 22 ? node.label.slice(0, 22) + "…" : node.label;
      ctx.font = `${isHub ? 11 : 9}px 'IBM Plex Mono', monospace`;
      ctx.fillStyle = isHub ? "#F8FAFC" : "#94A3B8";
      ctx.textAlign = "center";
      ctx.fillText(label, node.x, node.y + r + 9);
    }
  }, [selectedId, pathKey]);

  return (
    <div ref={wrap} className="w-full h-full" data-testid="network-graph">
      <ForceGraph2D
        ref={fgRef}
        width={dims.w}
        height={dims.h}
        graphData={graphData}
        backgroundColor="#050B14"
        nodeCanvasObject={paint}
        nodePointerAreaPaint={(node, color, ctx) => {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.kind === "origin" || node.kind === "center" ? 10 : 7, 0, 2 * Math.PI);
          ctx.fill();
        }}
        linkColor={(l) => (l.kind === "membership" ? "rgba(139,164,181,0.25)" : l.status === "disputed" || l.status === "inferred" ? "rgba(239,68,68,0.5)" : "rgba(18,122,116,0.6)")}
        linkWidth={(l) => (l.kind === "relationship" ? 1.2 : 0.6)}
        linkDirectionalParticles={0}
        onNodeClick={(n) => onSelect && onSelect(n)}
        cooldownTicks={80}
      />
    </div>
  );
}
